import { AxiosError, AxiosRequestConfig } from 'axios';

import apiClient from '@api-client';
import { QueryKey, UseQueryOptions, useQuery } from '@tanstack/react-query';

type ApiErrorData = {
  error: string;
  statusCode: number;
  message: string[];
};

type ApiError = {
  message: string[];
  data: ApiErrorData;
};

export type QueryOptions<R> = Omit<
  UseQueryOptions<R, AxiosError<ApiError, never>, R, QueryKey>,
  'queryKey' | 'queryFn'
>;

// Type utilities for deriving types from apiClient method signatures
// These can be used by consumers who want type-safe method-based inference:
// useApiClient<ParamsFor<'MethodName'>, ResponseFor<'MethodName'>>({method: 'MethodName', ...})
type ApiClientMethod<MK extends keyof typeof apiClient> =
  (typeof apiClient)[MK];

export type ParamsFor<MK extends keyof typeof apiClient> = Parameters<
  ApiClientMethod<MK>
>[0] extends AxiosRequestConfig | undefined
  ? undefined
  : Parameters<ApiClientMethod<MK>>[0];

export type ResponseFor<MK extends keyof typeof apiClient> = Awaited<
  ReturnType<ApiClientMethod<MK>>
>;

type ApiClientProps<P, R> = {
  method: keyof typeof apiClient;
  params?: P;
  queryKey?: QueryKey;
  config?: QueryOptions<R>;
  requestOptions?: AxiosRequestConfig;
};

export const useApiClient = <P = unknown, R = unknown>({
  queryKey,
  method,
  params,
  config = {},
  requestOptions,
}: ApiClientProps<P, R>) =>
  useQuery<R, AxiosError<ApiError, never>>({
    queryKey: queryKey || [method, params],
    queryFn: async () => {
      const fn = apiClient[method];
      // Use unknown intermediate type instead of any for type safety
      // The final assertion to R is safe because R is the declared generic type
      const args =
        params === undefined ? [requestOptions] : [params, requestOptions];
      const result = await (fn as (...a: unknown[]) => Promise<unknown>)(
        ...args
      );
      return result as R;
    },
    ...config,
  });
